import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { IChooserServerSideDataParams, IListItem, IServerSideRequestOptions } from '@bssh/comp-lib';
import { ColDef, GridApi, GridOptions, ICellRenderer, RowNode, SelectionChangedEvent } from "ag-grid-community";
import { debounceTime, distinctUntilChanged, filter, first, retryWhen, tap } from "rxjs/operators";

import { HttpClient } from "@angular/common/http";
import { FormControl } from "@angular/forms";
import IResource from "@app/core/model/resource";
import { SearchResourceType } from "@app/core/model/search/search-resource-type";
import { ISearchResultItem } from "@app/core/model/search/search-result-item";
import { genericRetryWhen } from "@app/core/rxjsutils/rxjs-utilities";
import { SearchResourceDictionaryService } from "@app/core/services/resource-dictionary/search-resource-dictionary.service";
import _ from "lodash";
import { SubSink } from "subsink";
import { IBioSample } from "@app/core/model/bioSamples/bioSample";
import { RadDropdownSelectRendererComponent } from "@app/shared/cell-renderers/rad-dropdown-select-renderer/rad-dropdown-select-renderer.component";
import { fromEvent, Subject } from "rxjs";
import { BsshDataTableComponent } from "@app/shared/bssh-data-table/bssh-data-table.component";
import { TableSettingType } from "@app/core/services/user/user-settings.service";
import { IColumnSetting, TableSettingsService } from "@app/core/services/table-settings/table-settings.service";
import { UserSettingsStore } from "@app/core/store/usersettings/user-settings.store";
import { IInitialColumnFilter } from "@app/shared/modals/model/action-modal";


export enum ResourceChooserMessageTypes {
    UpdateResourceChooserColumn = 'UPDATE_RESOURCE_CHOOSER_COLUMN'
}

@Component({
  selector: 'app-resource-chooser',
  templateUrl: './resource-chooser.component.html',
  styleUrls: ['./resource-chooser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceChooserComponent implements OnInit, AfterViewInit, OnDestroy {

  @HostBinding('attr.data-selection-type')
  get selectionType() {
    return this.rowSelection;
  }

  @Input()
  public title: string;
  @Input()
  public titleToolTip = "";
  @Input()
  public searchPlaceHolderText = "";
  @Input()
  public rowSelection: string;

  public columnDefs: ColDef[] = [];
  
  @Input()
  public rowData: any[] = [];

  @Input()
  public initiallySelectedItems: ISearchResultItem[] = [];

  @Input()
  public resourceType: SearchResourceType;

  @Input()
  public debounceTime: string = "300";

  @Input()
  public gridOptions: GridOptions;

  @Input()
  public trackLibraryPreps: boolean = false;

  @Input() disableHoverEffects: boolean = false;

  @Input() hideSelectionColumn: boolean = false;

  @HostBinding('attr.data-hide-selection')
  get hideSelection() {
    return this.hideSelectionColumn ? 'true' : 'false';
  }

  @HostBinding('attr.data-disable-hover')
  get disableHover() {
    return this.disableHoverEffects ? 'true' : 'false';
  }

  @Input() public isSearchApiData: boolean = true;

  public gridApi: GridApi;

  formControl: FormControl = new FormControl('');

  public selectAllTriggeredSubject: Subject<boolean> = new Subject();

  loadedInitialData: boolean = false;

  @HostBinding('style.--loading-bar-top') 
  get loadingBarTop(): string {
    return this.loadedInitialData ? '0px' : '80px';
  }
  // ────────────────────────────────────────────────────────────────────────────────
  // Matcher logic. This isn't currently working but carried over existing logic from ui-components for now.
  @Input()
  public hasMatcher: boolean = false;

  private subs = new SubSink();

  /**
   * To support server side row model in the data table.
   * If this is set, then the server side model will be used to fetch data in the chooser.
   * If Static row data(client side model) needs to be used, set `rowData` param instead
   */
  @Input() chooserServerSideDataParams: IChooserServerSideDataParams = null;
  @Input() newlyCreatedResourceId: string;
  @Input() tableId: string;

  /**
   * A pre-applied, user-removable column filter to seed on the table once the grid is ready
   * (e.g. the app-launch project-filter). Applied via the column's CustomFilter instance just
   * as a user click would, so it remains clearable via the column header.
   */
  @Input() initialColumnFilter: IInitialColumnFilter = null;

  private hasSeededInitialColumnFilter = false;

  private userSavedColumnsSettings: any;

  public serverSideRequestOptions: IServerSideRequestOptions;

  // Selected count
  public selectedItems: number = 0;

  // Track initially selected items that may not be loaded yet
  // string/key will be the Id of the resource
  private selectedItemsMap: Map<string, ISearchResultItem> = new Map();
  private currentlySelectedItems: ISearchResultItem[] = [];

  // Track library prep selections per biosample
  private libraryPrepSelections: Map<string, Set<string>> = new Map();

  get columnSettingsId(): string {
    return `${TableSettingType.Column}.${this.tableId}`;
  }

  @ViewChild("dataTable", { static: false }) dataTable: BsshDataTableComponent;


  // Outputs
  @Output() public itemsSelected: EventEmitter<any> = new EventEmitter<any>();

  // Private fields.
  private defaultSearchPlaceHolderText = "Search ...";

  constructor(
    private searchResourceDictionaryService: SearchResourceDictionaryService,
    private http: HttpClient,
    private tableSettingsService: TableSettingsService,
    private userSettingsStore: UserSettingsStore,
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    this.serverSideRequestOptions = this.chooserServerSideDataParams != null ? this.chooserServerSideDataParams.serverSideRequestOptions : null;

    if(this.newlyCreatedResourceId) {
      const { origin, path } = this.chooserServerSideDataParams.serverSideRequestOptions
      const { scope } = this.serverSideRequestOptions.params;

      const initialQuery = this.http.get(`${origin}/${path}?limit=1&offset=0&query=(id:${this.newlyCreatedResourceId})&scope=${scope}`).pipe(
        tap((response) => {
          if(!response['Items'] || response['Items'].length <= 0) {
            throw new Error(`Could not find resource with id '${this.newlyCreatedResourceId}'`);
          }
        }),
        retryWhen(genericRetryWhen({ maxRetryAttempts: 30 })),
        first()
      );
      this.serverSideRequestOptions.initialQuery = initialQuery;
    }

    this.titleToolTip = _.isEmpty(this.titleToolTip) ? this.title : this.titleToolTip;
    this.searchPlaceHolderText = _.isEmpty(this.searchPlaceHolderText) ? this.defaultSearchPlaceHolderText : this.searchPlaceHolderText;
    
    // Initialize the initially selected items map
    if (this.initiallySelectedItems && this.initiallySelectedItems.length > 0) {
      const selectedItems = this.rowSelection === 'single' ? this.initiallySelectedItems.slice(0, 1) : this.initiallySelectedItems;
      selectedItems.forEach(item => {
        const key = this.searchResourceDictionaryService.getSearchResultResource(item, this.resourceType).Id;
        this.selectedItemsMap.set(key, item);
      });
    }
  }

  ngAfterViewInit(): void {
    this.dataTable.setSelectAllCheckboxStatus = () => this.setSelectAllCheckboxStatus();

    this.subs.sink = this.formControl.valueChanges.pipe(
      debounceTime(parseInt(this.debounceTime, 10)),
      distinctUntilChanged(),
      tap((value) => {
        // Ag grid quick filtering only(for the most part) works with Client side row model.
        // If a server side row model is used, the data source should handle it.
        if (this.chooserServerSideDataParams) {
          // Invoke the call back function supplied in the server side request params.
          this.chooserServerSideDataParams.rawTextFilterProcessing(value, this.gridOptions);
        } else {
          this.gridApi.setQuickFilter(value);
        }
      })
    ).subscribe();

    this.subs.sink = this.selectAllTriggeredSubject.subscribe((isChecked) => {
      if(this.trackLibraryPreps && this.resourceType === SearchResourceType.Biosample) {
        this.gridApi.forEachNode(node => {
          const biosample = this.getResourceFromSearchResultItem(node.data) as IBioSample;
          const biosampleId = biosample.Id;

          if(biosample && biosample.LibraryPreps && biosample.LibraryPreps.length > 1) {
            if(isChecked) {
              const allLibraryPrepIds = biosample.LibraryPreps.map(libraryPrep => libraryPrep.Id);
              this.libraryPrepSelections.set(
                biosampleId, 
                new Set(allLibraryPrepIds)
              );
              this.updateLibraryPrepCellRenderer(biosampleId, allLibraryPrepIds);
            } else {
              this.libraryPrepSelections.delete(biosampleId);
              this.updateLibraryPrepCellRenderer(biosampleId, []);
            }

          }
        });
      }
    });
  }

  get columnSettings(): IColumnSetting[] {
    if(this.columnDefs) {
      return this.columnDefs.map((columnDefinition) => {
        return {
          Id: columnDefinition.colId,
          label: columnDefinition.colId,
          showAlways: columnDefinition['showAlways'],
        } as IColumnSetting;
      });
    } else {
      return [];
    }
  }

  onColumnVisibilityChanged($event: any) {
    //bypass this step during page load
    //data will be fetched in grid ready event
    if (this.gridOptions && this.gridOptions.api) {
      const selectionColumns = this.tableSettingsService.generateSelectionColumns(this.columnDefs);
      this.tableSettingsService.setColumnSettings(
        this.columnSettingsId as any,
        this.columnSettings,
        selectionColumns
      );
    }
  }

  private fetchTableSettings(): void {
    this.subs.sink = this.userSettingsStore.isLoggedInUserSettingsLoaded().subscribe({
      next: () => {
        this.userSavedColumnsSettings = this.tableSettingsService.getColumnSettings(
          this.columnSettingsId as any, 
          this.columnSettings
        );

        if(this.userSavedColumnsSettings) {
          this.tableSettingsService.setGridColumnsVisibilityDefaults(
            this.columnSettings,
            this.userSavedColumnsSettings,
            this.gridOptions,
            this.columnDefs
          );
        }
      },
      error: (error) => {
        console.log(`An error occurred initializing ${this.tableId}: ` + JSON.stringify(error));
      }
    })
  }

  public clearSelected(): void {
    this.currentlySelectedItems = [];
    this.selectedItemsMap.clear();
    this.clearLibraryPrepSelections();

    this.dataTable.selectedRows = this.getAllSelectedItems();
    this.dataTable.cdr.detectChanges();
  }

  public sizeColumnsToFit() {
    this.gridApi.sizeColumnsToFit();
  }

  public setPageSize(data) {
    this.gridApi.paginationSetPageSize(Number(data.option.value));
  }

  private getLastChangedNode(newlySelectedRows: any[]): RowNode | null {
    // Get current selected IDs
    const newlySelectedRowIds = new Set(
      newlySelectedRows.map(row => this.getResourceFromSearchResultItem(row).Id)
    );

    const currentlySelectedRowIds = new Set(
      this.currentlySelectedItems.map(item => this.getResourceFromSearchResultItem(item).Id)
    );
    
    // Find newly selected (in current but not in previous)
    for (const id of newlySelectedRowIds) {
      if (!currentlySelectedRowIds.has(id)) {
        return this.getRowNodeByResourceId(id);
      }
    }
    
    // Find newly deselected (in previous but not in current)
    for (const id of currentlySelectedRowIds) {
      if (!newlySelectedRowIds.has(id)) {
        return this.getRowNodeByResourceId(id);
      }
    }
    
    return null;
  }

  // api for callback takes from https://www.ag-grid.com/javascript-grid-events/#miscellaneous
  public onSelectionChanged($event: SelectionChangedEvent) {
    let gridSelectedRows = $event.api.getSelectedRows();
    
    // Handle bidirectional sync: row selection → library prep selection
    if (this.trackLibraryPreps && this.resourceType === SearchResourceType.Biosample) {
      // lastChangedRow now contains the data of the row that was last focused (likely just selected/deselected)
      const lastChangedNode = this.getLastChangedNode(gridSelectedRows);

      // Only if row has biosample with single library prep
      if(lastChangedNode) {
        if (lastChangedNode.isSelected()) {
          // If lastChangedRow selection value is true, open the library prep dropdown
          const biosample = this.getResourceFromSearchResultItem(lastChangedNode.data) as IBioSample;
          if (biosample && biosample.LibraryPreps && biosample.LibraryPreps.length > 1) {
            // Open the library prep dropdown for biosamples with multiple library preps
            this.openLibraryPrepDropdown(lastChangedNode);
            lastChangedNode.setSelected(false, false, true);
            gridSelectedRows = $event.api.getSelectedRows();
          }
        } else {
          this.updateLibraryPrepCellRenderer(lastChangedNode.data.BioSample.Id, []);
        }
      }

      this.syncLibraryPrepsFromRowSelection(gridSelectedRows);
    }

    // Update our tracking of currently selected items
    this.currentlySelectedItems = [...gridSelectedRows];
    
    this.synchronizeSelectedItemsMap();
  }

  private getAllSelectedItems(): any[] {
    // Combine grid selections with initially selected items that haven't been loaded
    const allSelected: ISearchResultItem[] = [...this.currentlySelectedItems];
    
    if (this.chooserServerSideDataParams) {
      // Add initially selected items that aren't currently visible in the grid
      this.selectedItemsMap.forEach((item, key) => {
        const alreadyInGrid = allSelected.some(selected => {
          const selectedKey = this.searchResourceDictionaryService.getSearchResultResource(selected, this.resourceType).Id;
          return selectedKey === key;
        });
        if (!alreadyInGrid) {
          allSelected.push(item);
        }
      });
    }
    
    if (this.trackLibraryPreps && this.resourceType === SearchResourceType.Biosample) {
      // Attach selected library prep IDs to the search result item (not the biosample domain model)
      allSelected.forEach(item => {
        const biosampleId = this.getResourceFromSearchResultItem(item).Id;
        const selectedLibraryPreps = this.libraryPrepSelections.get(biosampleId);
        
        if (selectedLibraryPreps) {
          item.selectedLibraryPrepIds = Array.from(selectedLibraryPreps);
        }
      });
    }
    
    return allSelected;
  }

  public onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
    this.gridOptions.api = gridApi; // Ensure gridOptions.api is set for table settings service
    this.columnDefs = this.gridOptions.columnDefs as ColDef[];
    this.setupSelectAllListener();
    this.setupHorizontalScrollAutoFix();

    this.fetchTableSettings();

    this.seedInitialColumnFilter();
    
    // Set grid context to provide callback to cell renderers
    if (this.trackLibraryPreps && this.resourceType === SearchResourceType.Biosample) {
      // Set context on the grid options to ensure it's available to cell renderers
      this.gridOptions.context.onLibraryPrepSelectionChange = (newValue, cellParams) => 
        this.onLibraryPrepSelectionChange(newValue, cellParams);
    }
    
    // For server-side model, listen to when rows are loaded
    if (this.chooserServerSideDataParams) {
      this.gridApi.addEventListener('modelUpdated', () => {
        if(!this.loadedInitialData) {
          setTimeout(() => {
            this.gridApi.sizeColumnsToFit();
          }, 0);
        }

        this.loadedInitialData = true;
        this.selectInitialItemsInCurrentView();

        setTimeout(() => {
          this.dataTable.selectedRows = this.getAllSelectedItems();
          this.dataTable.setSelectAllCheckboxStatus();
          this.dataTable.cdr.detectChanges();
        }, 0);
      });
    } else {
      // For client-side model, select immediately and size columns
      if (this.initiallySelectedItems && this.initiallySelectedItems.length > 0) {
        this.selectInitialItemsInCurrentView();
      }
      // Size columns after initial render for client-side model
      setTimeout(() => {
        if (this.gridApi) {
          this.gridApi.sizeColumnsToFit();
        }
      }, 0);
    }

    // Emit initial data to modal in case user wants to select initially selected data
    const allSelectedItems = this.getAllSelectedItems();
    this.itemsSelected.emit(allSelectedItems);

  }

  /**
   * Seeds a pre-applied, user-removable column filter on the grid (e.g. the app-launch
   * project-filter). Applies it through the column's CustomFilter instance via onChange - the
   * same path a user click would take - so the header label and selection reflect it and the
   * user can clear it via the column header. Runs once; deferred until the filter instance
   * exists (custom filters are created lazily) and guarded against re-running.
   */
  private seedInitialColumnFilter(): void {
    if (!this.initialColumnFilter || this.hasSeededInitialColumnFilter || !this.gridApi) {
      return;
    }
    this.hasSeededInitialColumnFilter = true;

    const { colId, option } = this.initialColumnFilter;

    setTimeout(() => {
      if (!this.gridApi) {
        return;
      }
      const filterInstance = this.gridApi.getFilterInstance(colId) as any;
      if (!filterInstance || typeof filterInstance.getFrameworkComponentInstance !== 'function') {
        return;
      }
      const component = filterInstance.getFrameworkComponentInstance();
      if (component && typeof component.onChange === 'function') {
        // Mimic a user selecting this filter option: sets the selection, updates the header
        // label, and fires filterChangedCallback so the grid re-queries with the filter applied.
        component.onChange(option);
      }
    }, 0);
  }

  // I'm overriding the 'setSelectAllCheckboxStatus' method on data table because I need a custom version for resource chooser
  public setSelectAllCheckboxStatus() {
    // Update header checkbox to reflect 'select-all' state or not
    const renderedNodes = this.dataTable.api.getRenderedNodes();
    // when single select option is specified, there is no 'Select All' check box.
    if (this.dataTable.selectAllRefs[0] != null) {
      const headerCheckboxRef =
        this.dataTable.selectAllRefs[0].querySelector(".checkbox__label");
      if (headerCheckboxRef) {
        const headerCheckboxClassList = headerCheckboxRef.classList;

        const selectableRenderedRows = renderedNodes.filter((renderedNode: RowNode) => renderedNode.selectable);

        const areAllRenderedRowsSelected = selectableRenderedRows.every(
          (renderedNode: RowNode) => {
            return this.dataTable.selectedRows.find((selectedRow: ISearchResultItem) => {
              const selectedNodeResource = this.getResourceFromSearchResultItem(selectedRow);
              const renderedNodeResource = this.getResourceFromSearchResultItem(renderedNode.data);
              return selectedNodeResource && renderedNodeResource && selectedNodeResource.Id === renderedNodeResource.Id;
            });
          }
        );

        if (areAllRenderedRowsSelected) {
          headerCheckboxClassList.add(this.dataTable.checkboxCheckedClass);
        } else if (
          headerCheckboxClassList.contains(this.dataTable.checkboxCheckedClass)
        ) {
          headerCheckboxClassList.remove(this.dataTable.checkboxCheckedClass);
        }
      }
    }
  }

  private setupSelectAllListener(): void {
    // Wait for grid to be ready
      const headerCheckbox = document.querySelector('.ag-header-select-all');
      if (headerCheckbox) {
        headerCheckbox.addEventListener('click', (event) => {
          setTimeout(() => {
            const span = headerCheckbox.querySelector('.checkbox__label');
            const isChecked = span.classList.contains('checkbox__label--checked');
            this.selectAllTriggeredSubject.next(isChecked);
          }, 50);
          
          // The span with the checked state usually has a class like 'checkbox__label--checked'
          // Find the span inside the headerCheckbox
        });
      }
  }

  // This function is an unfortunately necessary hack to get the ag-grid columns to resize if the user horizontally scrolls quickly.
  // For some reason the columns won't resize if the user scrolls quickly. Scrolling slowly doesn't exhibit the same problem for unknown
  // reasons.
  private setupHorizontalScrollAutoFix(): void {
    // Find the horizontal scroll viewport element
    const horizontalScrollViewport = this.elementRef.nativeElement.querySelector('.ag-body-horizontal-scroll-viewport');
    
    if (horizontalScrollViewport) {
      let isProgrammaticScroll = false;
      
      // Create observable from scroll events
      this.subs.sink = fromEvent(horizontalScrollViewport, 'scroll')
        .pipe(
          // Ignore programmatic scrolls (the ones we trigger)
          filter(() => !isProgrammaticScroll),
          // Wait for user to stop scrolling
          debounceTime(100)
        )
        .subscribe(() => {
          // Capture current scroll position
          const currentScrollLeft = horizontalScrollViewport.scrollLeft;
          
          // Set flag to prevent our programmatic scrolls from triggering the observable
          isProgrammaticScroll = true;
          
          // Trigger a tiny scroll event to force ag-grid to recalculate columns
          // Scroll 1px left then back to original position
          horizontalScrollViewport.scrollLeft = currentScrollLeft - 1;
          
          // Use requestAnimationFrame to ensure the scroll is applied before scrolling back
          requestAnimationFrame(() => {
            horizontalScrollViewport.scrollLeft = currentScrollLeft;
            
            // Reset flag after both programmatic scrolls are complete
            requestAnimationFrame(() => {
              isProgrammaticScroll = false;
            });
          });
        });
    }
  }

  getResourceFromSearchResultItem(searchResultItem: ISearchResultItem): Partial<IResource> {
    if(searchResultItem) {
      return this.searchResourceDictionaryService.getSearchResultResource(searchResultItem, this.resourceType);
    } else {
      return null;
    }
  }

  public onLibraryPrepSelectionChange(
    selectedLibraryPrepIds: string[], 
    params: any
  ): void {
    const biosampleId = this.getResourceFromSearchResultItem(params.data).Id;
    const node = params.node as RowNode;
    
    // Update library prep selections map
    if (selectedLibraryPrepIds && selectedLibraryPrepIds.length > 0) {
      this.libraryPrepSelections.set(
        biosampleId, 
        new Set(selectedLibraryPrepIds)
      );
      
      // Select the row if any library prep is selected
      node.setSelected(true, false, true);
    } else {
      // If no library preps selected, remove from map and deselect row
      this.libraryPrepSelections.delete(biosampleId);
      node.setSelected(false, false, true);
    }
    const gridSelectedRows = this.gridApi.getSelectedRows();
    this.currentlySelectedItems = [...gridSelectedRows];

    this.synchronizeSelectedItemsMap();
  }

  private synchronizeSelectedItemsMap() {
    // For server-side model, also track which initially selected items were deselected
    if (this.chooserServerSideDataParams) {

      if(this.rowSelection === 'single' && this.currentlySelectedItems.length > 0) {
        const selectedItem = this.currentlySelectedItems[0];
        const key = this.searchResourceDictionaryService.getSearchResultResource(selectedItem, this.resourceType).Id;
        this.selectedItemsMap.clear();
        this.selectedItemsMap.set(key, selectedItem);
      } else {
        // Remove items that were deselected
        this.gridApi.forEachNode(node => {
          if (!node.isSelected() && node.data) {
            const key = this.searchResourceDictionaryService.getSearchResultResource(node.data, this.resourceType).Id;
            if (key && this.selectedItemsMap.has(key)) {
              this.selectedItemsMap.delete(key);
            }
          // Add items that were recently selected to remember in map
          } else if(node.isSelected()) {
            const key = this.searchResourceDictionaryService.getSearchResultResource(node.data, this.resourceType).Id;
            this.selectedItemsMap.set(key, node.data);
          }
        });
      }
    }

    const allSelectedItems = this.getAllSelectedItems();
    setTimeout(() => {
      this.dataTable.selectedRows = allSelectedItems;
      this.dataTable.setSelectAllCheckboxStatus();
      this.dataTable.cdr.detectChanges();
    }, 50); // Do not alter this number. It must be after 40 because that's the debounceTime we're using for OnSelectionChange in Data Table component


    this.itemsSelected.emit(allSelectedItems);
  }

  private syncLibraryPrepsFromRowSelection(selectedRows: any): void {
    selectedRows.forEach(row => {
      const biosample = this.getResourceFromSearchResultItem(row) as IBioSample;
      const biosampleId = biosample.Id;
      
      if (biosample && biosample.LibraryPreps && biosample.LibraryPreps.length === 1) {
        // Select ALL library preps when row is selected
        const allLibraryPrepIds = biosample.LibraryPreps.map(libraryPrep => libraryPrep.Id);
        this.libraryPrepSelections.set(
          biosampleId, 
          new Set(allLibraryPrepIds)
        );
      } 
      // We'll need to add an extra variable to alert that we've used the select all option
      // Only in that case will we need to update the prepSelection and the cell renderer
      else if (biosample && biosample.LibraryPreps && biosample.LibraryPreps.length > 1) {
        // We take care of this case in selectAllTriggeredSubject side effects
      }
    });
    
    // Clean up deselected rows
    this.cleanupDeselectedLibraryPreps(selectedRows);

  }

  private openLibraryPrepDropdown(node: RowNode): void {
    // Find the cell renderer instance for the LibraryPrepKit column
    const cellRendererInstances = this.gridApi.getCellRendererInstances({
      rowNodes: [node],
      columns: ['LibraryPrepKit']
    });
    
    if (cellRendererInstances && cellRendererInstances.length > 0) {
      const cellRendererWrapper = cellRendererInstances[0];
      const componentInstance = (cellRendererWrapper as any).getFrameworkComponentInstance() as RadDropdownSelectRendererComponent;
      componentInstance.showDropdown();
    }
  }

  private getRowNodeByResourceId(resourceId: string): RowNode | null {
    let foundNode: RowNode | null = null;
    
    this.gridApi.forEachNode(node => {
      if (node.data) {
        const nodeResourceId = this.getResourceFromSearchResultItem(node.data).Id;
        if (nodeResourceId === resourceId) {
          foundNode = node;
        }
      }
    });
    
    return foundNode;
  }

  private updateLibraryPrepCellRenderer(
    biosampleId: string, 
    selectedIds: string[]
  ): void {
    const rowNode = this.getRowNodeByResourceId(biosampleId);
    if(rowNode) {
      rowNode.data.selectedLibraryPrepIds = selectedIds;
    
      // Refresh the cell to update the dropdown with new values
      this.gridApi.refreshCells({
        rowNodes: [rowNode],
        columns: ['LibraryPrepKit'], // Using the column ID from BiosamplesSortFields
      });
    }
    
  }

  private clearLibraryPrepSelections(): void {
    this.libraryPrepSelections.forEach((_, biosampleId) => {
      this.updateLibraryPrepCellRenderer(biosampleId, []);
    });
    this.libraryPrepSelections.clear();
    
  }

  private cleanupDeselectedLibraryPreps(selectedRows: any[]): void {
    const selectedBiosampleIds = new Set(
      selectedRows.map(row => this.getResourceFromSearchResultItem(row).Id)
    );
    
    // Remove library prep selections for deselected biosamples
    this.libraryPrepSelections.forEach((_, biosampleId) => {
      if (!selectedBiosampleIds.has(biosampleId)) {
        this.libraryPrepSelections.delete(biosampleId);
        this.updateLibraryPrepCellRenderer(biosampleId, []);
      }
    });
  }

  private selectInitialItemsInCurrentView(): void {
    this.gridApi.forEachNode(node => {
      if(node.data) {
        const key = this.searchResourceDictionaryService.getSearchResultResource(node.data, this.resourceType).Id;
        if (key && this.selectedItemsMap.has(key)) {
          node.setSelected(true, false, true);
          
          // Restore library prep selections for biosamples
          if (this.trackLibraryPreps && this.resourceType === SearchResourceType.Biosample) {
            const initialItem = this.selectedItemsMap.get(key);
            if (initialItem && initialItem.selectedLibraryPrepIds && initialItem.selectedLibraryPrepIds.length > 0) {
              // Restore the library prep selections
              this.libraryPrepSelections.set(
                key,
                new Set(initialItem.selectedLibraryPrepIds)
              );
              
              // Update the node data to reflect the selected library preps
              this.updateLibraryPrepCellRenderer(node.data.BioSample.Id, initialItem.selectedLibraryPrepIds);
            }
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

}
